/**
 * Schedule View — reading the /schedule-view/alerts payload.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── THE PAYLOAD ─────────────────────────────────────────────────────────────────────────────
 *   { date, byPatNum: { "<PatNum>": { <key>: <detail object> } }, ran: [...], errors: [...] }
 *
 * `ran` and `errors` are the important part and the reason this file exists. An alert that FAILED
 * and an alert that found NOTHING both produce an empty `byPatNum`, and on screen both look like
 * "nobody owes you money". Only `ran`/`errors` can tell them apart, so every count this file
 * produces carries whether it is trustworthy.
 *
 * ── WHY A CARD IS DRAWN FROM `ran`, NOT FROM THE SETTINGS ───────────────────────────────────
 * The settings say what the office WANTS. `ran` says what the server actually computed. Wave 1
 * implements five of the eleven alerts, so a card driven by the settings would show "Medical
 * history due: 0" for every practice — a confident, permanent, wrong answer. Driving it from `ran`
 * means each card appears exactly when it starts being true, with no second list to update.
 */

/**
 * How many patients one `array-contains-any` listener may ask about.
 *
 * ⛔ MEASURED, not assumed. Firestore rejects 31 with
 * `'ARRAY_CONTAINS_ANY' supports up to 30 comparison values`, and the limit is enforced by the
 * BACKEND — the same cap applies to the browser SDK as to the Admin SDK.
 *
 * ⚠️ PracticePulse's helper caps at 10 and openly admits that an 11th patient silently loses their
 * badge (`PracticePulse.vue`). That was a v9-era limit which has since been raised; it is not a
 * deliberate choice, and copying it would have opened three times the listeners needed.
 *
 * ⚠️ The query also carries `deleted != true`. An inequality is itself a disjunction, so it looked
 * as though the two would multiply out and halve the usable cap — they do not. Firestore counts
 * this limit against the array values alone, which was confirmed by probing dev rather than
 * reasoned about.
 */
export const TASK_CHUNK_SIZE = 30;

/**
 * Split the day's patients into listener-sized groups.
 *
 * A 40-80 appointment day therefore needs 2-3 listeners rather than the 4-8 a cap of 10 would
 * have forced. Non-positive and junk sizes fall back to the real cap rather than producing an
 * infinite loop or a single oversized query Firestore would reject.
 */
export function chunkPatNums(patNums, size) {
  const list = Array.isArray(patNums) ? patNums.filter(n => n !== null && n !== undefined) : [];
  const n = Number(size);
  const step = (Number.isInteger(n) && n > 0) ? Math.min(n, TASK_CHUNK_SIZE) : TASK_CHUNK_SIZE;
  const out = [];
  for (let i = 0; i < list.length; i += step) out.push(list.slice(i, i + step));
  return out;
}

/**
 * Fold the live task counts into the server's alert payload.
 *
 * ⛔ `task` is the one alert the server does not compute, because it has to change the INSTANT
 * somebody tags a patient — a request/response endpoint would answer with whatever was true when
 * the page last asked. So it arrives from a Firestore listener and is merged in here, where every
 * consumer (blocks, tooltips, the summary strip) sees one payload shape and needs no special case.
 *
 * ⛔ `task` is ALSO appended to `ran`. `visibleAlertKeys` filters on `ran` so that a rule which
 * FAILED shows no icon anywhere rather than looking clean on some blocks — an alert missing from
 * `ran` is invisible. Merging the counts without this would produce a payload that silently drew
 * nothing.
 *
 * Returns the payload unchanged when there is nothing live to add, so an office with the alert
 * switched off, or a browser with no Firestore, is byte-for-byte what it was before.
 */
export function mergeTaskAlerts(payload, taskCounts) {
  if (!payload || !taskCounts) return payload;

  const patNums = Object.keys(taskCounts);
  if (!patNums.length && (payload.ran || []).indexOf('task') !== -1) return payload;

  const byPatNum = {};
  Object.keys(payload.byPatNum || {}).forEach((p) => {
    byPatNum[p] = Object.assign({}, payload.byPatNum[p]);
  });

  patNums.forEach((patNum) => {
    const count = Number(taskCounts[patNum]) || 0;
    if (count <= 0) return;
    if (!byPatNum[patNum]) byPatNum[patNum] = {};
    byPatNum[patNum].task = { count };
  });

  const ran = (payload.ran || []).slice();
  if (ran.indexOf('task') === -1) ran.push('task');

  return Object.assign({}, payload, { byPatNum, ran });
}

/**
 * Fold a TARGETED `/alerts` answer (`keys: [...]`, Phase 3 live refresh) into the full payload on
 * screen. Returns the new payload, or null when there is nothing to fold it into.
 *
 * ── ⛔ NEVER INSTALL A PARTIAL PAYLOAD ON ITS OWN ──────────────────────────────────────────────
 * A keyed answer's `ran` lists only the keys asked for, and every block draws only keys in `ran`
 * (visibleAlertKeys). Installing it as the payload would blank every other icon on the page until
 * the ten-minute clock. So: no full payload for that date ⇒ null, and the caller fetches in full.
 *
 * ── HOW A KEY IS REPLACED ──────────────────────────────────────────────────────────────────────
 * `partial.ran` is the replacement set (narrowed to `onlyKeys` when given): for each of those keys,
 * every patient's old entry is dropped and the new one applied — so a patient whose paperwork just
 * came back LOSES the icon, which is the whole point. Keys not replaced are untouched.
 *   • `ran` only ever GROWS here: a union. A targeted refresh must never hide a rule it did not ask
 *     about.
 *   • A keyed rule that FAILED keeps its stale entries and joins `errors` ("stale beats blank", the
 *     same choice fetchAlerts makes for a whole failed request). The summary strip reports it.
 *   • `task` is ignored in both directions: it is Firestore-only and folded in by mergeTaskAlerts.
 *   • Everything is a NEW object — Vue 2 does not see a property deleted in place.
 */
export function mergeAlertPayload(current, partial, onlyKeys) {
  if (!current || !partial || current.date !== partial.date) return null;
  const allowed = key => key !== 'task' && (!Array.isArray(onlyKeys) || onlyKeys.indexOf(key) !== -1);
  const replaced = (partial.ran || []).filter(allowed);
  const failed = (partial.errors || []).filter(allowed);

  const byPatNum = {};
  Object.keys(current.byPatNum || {}).forEach((patNum) => {
    const entry = Object.assign({}, current.byPatNum[patNum]);
    replaced.forEach((key) => { delete entry[key]; }); // on the copy
    if (Object.keys(entry).length) byPatNum[patNum] = entry;
  });
  Object.keys(partial.byPatNum || {}).forEach((patNum) => {
    const incoming = partial.byPatNum[patNum] || {};
    const picked = {};
    replaced.forEach((key) => { if (incoming[key] !== undefined) picked[key] = incoming[key]; });
    if (Object.keys(picked).length) byPatNum[patNum] = Object.assign({}, byPatNum[patNum] || {}, picked);
  });

  const ran = (current.ran || []).slice();
  replaced.forEach((key) => { if (ran.indexOf(key) === -1) ran.push(key); });
  const errors = (current.errors || []).filter(key => replaced.indexOf(key) === -1);
  failed.forEach((key) => { if (errors.indexOf(key) === -1) errors.push(key); });

  return Object.assign({}, current, { byPatNum, ran, errors });
}

/** A detail is always an object, so presence is the test — never truthiness of a count. */
export function hasAlert(detail) {
  return !!detail && typeof detail === 'object';
}

/** Every alert key recorded for one patient, in the payload's own order. */
export function alertsForPatient(payload, patNum) {
  if (!payload || !payload.byPatNum) return {};
  const entry = payload.byPatNum[String(patNum)];
  return entry && typeof entry === 'object' ? entry : {};
}

/**
 * The alert keys to draw on one appointment's block, in `order`.
 *
 * Filtered by what the server actually ran: if a rule failed, its icon is absent everywhere rather
 * than absent-looking-like-clean on some blocks.
 */
export function visibleAlertKeys(payload, patNum, order) {
  const forPatient = alertsForPatient(payload, patNum);
  const ran = (payload && payload.ran) || [];
  return (order || []).filter(key => hasAlert(forPatient[key]) && ran.indexOf(key) !== -1);
}

/**
 * THE alert keys one appointment shows — the block's icon row, the phone card's icon row, the
 * checklist chip and the checklist card (Phase 10e), and the Table view's rows (Phase 11).
 *
 * ⛔ ONE implementation, on purpose. These surfaces used to each compute "which alerts does this
 * patient have" on their own; a second copy drifting by a single filter (the office's own switch, or
 * the `ran` list) is a block with no balance icon beside a card that lists the balance — and staff
 * cannot tell which to believe with the patient standing at the desk. Guarded by
 * cypress/node-tests/schedule-view-checklist.test.js ("AGREEMENT") and a source scan that no
 * Schedule View component filters `visibleAlertKeys` by itself.
 *
 * Only alerts the office has switched ON (`viewSettings.alerts[key] !== false` — absent = on) AND the
 * server actually computed (`ran`; a rule that failed is absent everywhere, never absent-looking-
 * clean on some blocks). `order` is ALERT_KEYS, passed in because this file stays import-free.
 */
export function blockAlertKeys(payload, patNum, viewSettings, order) {
  if (!payload) return [];
  const wanted = (viewSettings && viewSettings.alerts) || {};
  return visibleAlertKeys(payload, patNum, order).filter(key => wanted[key] !== false);
}

/** Did any alert fail to compute for this day? */
export function hasAlertErrors(payload) {
  return !!(payload && payload.errors && payload.errors.length);
}

/**
 * Money, the way a practice writes it. Whole dollars — an appointment block is not a statement,
 * and "$1,240" reads at a glance where "$1,240.37" does not.
 */
export function formatMoney(amount) {
  // null, undefined and '' must NOT become "$0": Number('') and Number(null) are both 0, which is
  // a JavaScript quirk rather than anyone's intent, and "$0 outstanding" is a confident claim made
  // from a missing value. A real 0 still formats, because a genuine zero is worth saying.
  if (amount === null || amount === undefined || amount === '') return '';
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * The sentence shown in an alert's tooltip.
 *
 * Each one says what is true and, where it matters, what it does NOT say. `premed` is the case
 * that matters clinically: Open Dental records that a patient REQUIRES premedication and nothing
 * anywhere records whether they took it, so the tooltip must not let anyone infer that it did.
 */
export function describeAlert(key, detail) {
  const d = detail || {};
  switch (key) {
    case 'recall': {
      const days = Number(d.daysOverdue) || 0;
      if (days <= 0) return 'Due for a cleaning, with none booked.';
      return `Overdue for a cleaning by ${plural(days, 'day', 'days')}, with none booked.`;
    }
    case 'unsched': {
      const n = Number(d.procedures) || 0;
      const amount = formatMoney(d.amount);
      const head = `Here for a cleaning, with ${plural(n, 'treatment', 'treatments')} never scheduled`;
      return amount ? `${head} (${amount}).` : `${head}.`;
    }
    case 'balance': {
      const who = d.guarantor && d.guarantor.LName
        ? ` on ${d.guarantor.LName}'s account`
        : '';
      // The family's balance, said out loud: two siblings on the same day both show this figure,
      // and it is ONE debt, not two.
      return `${formatMoney(d.amount)} outstanding${who} (family balance).`;
    }
    case 'claim': {
      const days = Number(d.daysOutstanding) || 0;
      const n = Number(d.claims) || 0;
      return `${plural(n, 'claim', 'claims')} unpaid, oldest sent ${plural(days, 'day', 'days')} ago.`;
    }
    case 'premed':
      // Deliberately "requires", never "has taken". Nothing records the latter.
      return 'Requires premedication before treatment.';
    case 'followup': {
      const n = Number(d.count) || 0;
      const kinds = Array.isArray(d.types) && d.types.length ? ` (${d.types.join(', ')})` : '';
      // "Past its follow-up date" rather than "overdue": a follow-up with no date set is normal,
      // and calling those overdue would make the common case look like a failure.
      const late = d.pastDue ? ', past its follow-up date' : '';
      return `${plural(n, 'open follow-up', 'open follow-ups')}${kinds}${late}.`;
    }
    case 'medhx': {
      const days = Number(d.daysOverdue);
      const every = Number(d.intervalMonths) || 0;
      const cadence = every ? ` (this office asks every ${plural(every, 'month', 'months')})` : '';
      // Due exactly today is "due", not "overdue by 0 days".
      if (!Number.isFinite(days) || days <= 0) return `Medical history update due${cadence}.`;
      return `Medical history update overdue by ${plural(days, 'day', 'days')}${cadence}.`;
    }
    case 'txsign': {
      const n = Number(d.plans) || 0;
      const head = `${plural(n, 'treatment plan', 'treatment plans')} awaiting signature`;
      // ⛔ The useful half. "Sent but never opened" and "opened and not signed" are completely
      // different conversations to have with the patient, and the icon alone cannot say which.
      return d.opened ? `${head}, opened but not signed.` : `${head}, never opened.`;
    }
    case 'task': {
      const n = Number(d.count) || 0;
      // ⛔ A COUNT, never the task's title. Firestore bypasses the PHI demo mask (it works at the
      // axios boundary), and a task title routinely contains a patient's name — so putting one in
      // this tooltip would show real names on the page most likely to be demoed and recorded.
      return `${plural(n, 'open task', 'open tasks')} mentioning this patient.`;
    }
    case 'forms': {
      // ⛔ Per-item lists first (schedule-view-service, 2026-09-23). One `oldestSentAt` per patient
      // cannot say WHICH item was sent, so a sent form plus a never-sent new-patient packet read
      // "Waiting on Form, New Patient Packet #3" — telling staff to chase a patient for a packet
      // nobody ever gave them. The server now puts every item in exactly one list.
      const waiting = Array.isArray(d.waiting) ? d.waiting : null;
      const notSent = Array.isArray(d.notSent) ? d.notSent : null;
      if (waiting && notSent && (waiting.length || notSent.length)) {
        const parts = [];
        if (notSent.length) {
          const list = notSent.join(', ');
          if (waiting.length) parts.push(`Not sent yet: ${list}.`);
          // Only-not-sent keeps the specific sentences. Here `waiting` is empty, so the flags can
          // only describe items in `notSent`: a new-patient packet, a consent for today, or both.
          else if (d.newPatientPacket && !d.consentForVisit) parts.push('New patient — their new-patient packet has not been sent yet.');
          else if (d.consentForVisit && !d.newPatientPacket) parts.push(`Today's treatment needs ${list} — not sent to the patient yet.`);
          else parts.push(`Not sent yet: ${list}.`);
        }
        if (waiting.length) parts.push(`Waiting on ${waiting.join(', ')}.`);
        return parts.join(' ');
      }

      // A payload from a service that predates the lists: the single-discriminator rule below.
      const items = [].concat(
        Array.isArray(d.forms) ? d.forms : [],
        Array.isArray(d.packets) ? d.packets : [],
      );
      const n = Number(d.count) || items.length;
      // Named where we can name them: "waiting on 2 forms" tells the front desk to go looking,
      // "waiting on Consent" lets them just ask.
      const named = items.length ? items.join(', ') : plural(n, 'form', 'forms');
      // ⛔ "we have not sent it yet" and "we sent it and they have not done it" are OPPOSITE jobs.
      // The first is the practice's to fix before the patient sits down; the second is the
      // patient's, and the front desk chases it differently. Merging them into one sentence would
      // send staff to nag a patient about paperwork nobody ever gave them. `oldestSentAt` is the
      // discriminator: nothing was sent, so nothing can be "waited on".
      if (!d.oldestSentAt) {
        if (d.newPatientPacket) return 'New patient — their new-patient packet has not been sent yet.';
        if (d.consentForVisit) return `Today's treatment needs ${named} — not sent to the patient yet.`;
      }
      return `Waiting on ${named}.`;
    }
    case 'insverify': {
      // ⛔ "Never verified" is NOT "stale". They are different jobs, and the server keeps them
      // apart deliberately — saying "last verified 0 days ago" for a patient nobody has ever
      // checked would be a confident, wrong answer.
      if (d.neverVerified) return 'Insurance has never been verified.';
      const days = Number(d.ageDays);
      if (!Number.isFinite(days)) return 'Insurance needs verifying.';
      return `Insurance last verified ${plural(days, 'day', 'days')} ago.`;
    }
    default:
      return '';
  }
}

/**
 * Counts for the summary strip.
 *
 * Status counts come from the appointments themselves; alert counts are per APPOINTMENT (not per
 * patient), because the strip sits above a day and the office reads it as "how many of today's
 * appointments need something". A patient booked twice legitimately counts twice there.
 *
 * @param {Array}    appointments  the day's appointments (already filtered or not — caller's choice)
 * @param {Object}   payload       the /alerts payload
 * @param {Function} bucketOf      appt -> status bucket. Injected so this file stays import-free.
 */
export function summaryCounts(appointments, payload, bucketOf) {
  const appts = appointments || [];
  const resolve = typeof bucketOf === 'function' ? bucketOf : () => 'unconfirmed';
  const ran = (payload && payload.ran) || [];

  const statuses = { confirmed: 0, unconfirmed: 0, arrived: 0, completed: 0 };
  const alerts = {};
  ran.forEach((key) => { alerts[key] = 0; });

  appts.forEach((appt) => {
    const bucket = resolve(appt);
    if (Object.prototype.hasOwnProperty.call(statuses, bucket)) statuses[bucket] += 1;

    const forPatient = alertsForPatient(payload, appt.PatNum);
    ran.forEach((key) => {
      if (hasAlert(forPatient[key])) alerts[key] += 1;
    });
  });

  return { total: appts.length, statuses, alerts, ran, errors: (payload && payload.errors) || [] };
}

/**
 * The total amount behind a money alert across the day, for the card's subtitle.
 *
 * Counted ONCE PER PATIENT, not once per appointment — a family balance shown on two siblings'
 * blocks is one debt, and summing per appointment would overstate what the practice is owed.
 * For `balance` it is deduplicated per GUARANTOR for the same reason, one step further out.
 */
export function alertTotal(appointments, payload, key) {
  const seenPat = [];
  const seenGuarantor = [];
  let total = 0;

  (appointments || []).forEach((appt) => {
    const detail = alertsForPatient(payload, appt.PatNum)[key];
    if (!hasAlert(detail)) return;

    const patKey = String(appt.PatNum);
    if (seenPat.indexOf(patKey) !== -1) return;
    seenPat.push(patKey);

    if (key === 'balance') {
      const g = detail.guarantor && detail.guarantor.PatNum;
      const gKey = String(g === undefined || g === null ? patKey : g);
      if (seenGuarantor.indexOf(gKey) !== -1) return;
      seenGuarantor.push(gKey);
    }

    const amount = Number(detail.amount);
    if (Number.isFinite(amount)) total += amount;
  });

  return total;
}
